import { CodeBlock } from '@tiptap/extension-code-block'
import { Extension, wrappingInputRule } from '@tiptap/core'
import Heading from '@tiptap/extension-heading'
import { TaskItem } from '@tiptap/extension-task-item'
import { slugifyHeading } from '@/lib/heading-utils'
import { renderMermaidDiagram } from '@/lib/mermaid'
import { openMermaidInChat } from '@/lib/mermaid-chat'
import { openMermaidLightbox } from '@/lib/mermaid-lightbox'
import {
  isStatusBoardFenceLanguage,
  parseStatusBoardFence,
} from '@/lib/status-board-fence'
import { isBasecampDraftFenceLanguage } from '@/lib/basecamp-draft-fence'
import { buildBasecampDraftFenceDom } from '@/components/features/basecamp-draft-fence-view'

// Module-level callback — set per mounted editor instance so the static
// CustomKeyboardExtension can open the find bar without closing over React state.
export let _openFindBar: (() => void) | null = null

export function setOpenFindBarCallback(callback: (() => void) | null) {
  _openFindBar = callback
}

const MERMAID_EXPAND_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>`
const MERMAID_CHAT_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M12 7v6"/><path d="M9 10h6"/></svg>`

// CodeBlock extension extended to render mermaid diagrams inline.
// Non-mermaid blocks fall through to the default pre/code rendering.
/** True when a code block is a review-changelog typed YAML fence. */
function isReviewChangelogBlock(language: string, text: string): boolean {
  if (language === 'review-changelog') return true
  return language === 'yaml' && /entries:\s*\n/i.test(text) && /action:/i.test(text)
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Human-readable Hot pickups / Blocked card for ```yaml status-board fences. */
function buildStatusBoardFenceDom(text: string): HTMLElement {
  const parsed = parseStatusBoardFence(text)
  const root = document.createElement('div')
  root.className =
    'status-board-fence-view rounded-lg border border-border/50 bg-muted/20 px-3 py-3 my-2 select-none'
  root.setAttribute('data-testid', 'status-board-fence-view')

  if (!parsed) {
    root.className += ' text-muted-foreground text-sm'
    root.textContent = 'Status board fence (unparseable)'
    return root
  }

  const header = document.createElement('div')
  header.className = 'flex items-center justify-between gap-2 mb-2'
  header.innerHTML = `<span class="text-sm font-semibold text-foreground">Hot pickups · ${parsed.hotPickups.length}</span>
    <span class="text-xs text-muted-foreground">Updated ${escapeHtml(parsed.lastBoardUpdate || '—')}</span>`
  root.appendChild(header)

  if (parsed.hotPickups.length === 0) {
    const empty = document.createElement('p')
    empty.className = 'text-xs text-muted-foreground mb-2'
    empty.textContent = 'No hot pickups'
    root.appendChild(empty)
  } else {
    const list = document.createElement('ul')
    list.className = 'divide-y divide-border/40 mb-2'
    for (const row of parsed.hotPickups) {
      const li = document.createElement('li')
      li.className = 'py-2'
      const title = document.createElement('div')
      title.className = 'text-sm text-foreground font-medium'
      title.textContent = row.item
      const next = document.createElement('div')
      next.className = 'text-xs text-muted-foreground mt-0.5'
      next.textContent = row.next || '—'
      li.appendChild(title)
      li.appendChild(next)
      if (row.noteDate || row.bet) {
        const meta = document.createElement('div')
        meta.className = 'text-[11px] text-muted-foreground/80 mt-0.5'
        meta.textContent = [row.noteDate, row.bet].filter(Boolean).join(' · ')
        li.appendChild(meta)
      }
      list.appendChild(li)
    }
    root.appendChild(list)
  }

  if (parsed.blocked.length > 0) {
    const blockedHead = document.createElement('div')
    blockedHead.className = 'text-xs font-semibold text-foreground mt-2 mb-1'
    blockedHead.textContent = `Blocked · ${parsed.blocked.length}`
    root.appendChild(blockedHead)
    const blist = document.createElement('ul')
    blist.className = 'space-y-1'
    for (const row of parsed.blocked) {
      const li = document.createElement('li')
      li.className = 'text-xs text-muted-foreground'
      li.textContent = `${row.item} - ${row.blockedOn}${row.owner ? ` (${row.owner})` : ''}`
      blist.appendChild(li)
    }
    root.appendChild(blist)
  }

  const hint = document.createElement('p')
  hint.className = 'text-[11px] text-muted-foreground mt-2'
  hint.textContent = 'Edit the yaml status-board fence in Source mode'
  root.appendChild(hint)

  return root
}

export const MermaidCodeBlock = CodeBlock.extend({
  addNodeView() {
    return ({ node, editor }) => {
      const language: string = node.attrs.language || ''
      const text = node.textContent || ''

      // Reading mode draws the card in MarkdownPreview. While this editor is
      // editable, keep a normal code block so the fence source can be changed.
      if (isBasecampDraftFenceLanguage(language, text) && !editor.isEditable) {
        const wrapper = document.createElement('div')
        wrapper.className = 'basecamp-draft-fence-wrapper'
        const view = buildBasecampDraftFenceDom(text, language)
        const pre = document.createElement('pre')
        pre.style.display = 'none'
        const code = document.createElement('code')
        if (language) code.className = `language-${language}`
        pre.appendChild(code)
        wrapper.appendChild(view)
        wrapper.appendChild(pre)
        return { dom: wrapper, contentDOM: code }
      }

      if (isStatusBoardFenceLanguage(language, text)) {
        // Keep a hidden contentDOM so Visual->markdown round-trip preserves the fence.
        const wrapper = document.createElement('div')
        wrapper.className = 'status-board-fence-wrapper'
        const view = buildStatusBoardFenceDom(text)
        const pre = document.createElement('pre')
        pre.style.display = 'none'
        const code = document.createElement('code')
        if (language) code.className = `language-${language}`
        pre.appendChild(code)
        wrapper.appendChild(view)
        wrapper.appendChild(pre)
        return { dom: wrapper, contentDOM: code }
      }

      if (isReviewChangelogBlock(language, text) && !editor.isEditable) {
        const hidden = document.createElement('div')
        hidden.setAttribute('data-review-changelog', 'hidden')
        hidden.style.display = 'none'
        return { dom: hidden, contentDOM: null }
      }

      if (language !== 'mermaid') {
        // Regular code block — use TipTap's default DOM rendering
        const pre = document.createElement('pre')
        const code = document.createElement('code')
        if (language) code.className = `language-${language}`
        pre.appendChild(code)
        return { dom: pre, contentDOM: code }
      }

      // --- Mermaid block ---
      const wrapper = document.createElement('div')
      wrapper.className = 'mermaid-block'

      const actionsEl = document.createElement('div')
      actionsEl.className = 'mermaid-block-actions'

      const diagramEl = document.createElement('div')
      diagramEl.className = 'mermaid-diagram'

      const errorEl = document.createElement('div')
      errorEl.className = 'mermaid-error'
      errorEl.setAttribute('role', 'status')
      errorEl.style.display = 'none'

      const sourceEl = document.createElement('pre')
      sourceEl.className = 'mermaid-source'
      sourceEl.style.display = 'none'
      const sourceCode = document.createElement('code')
      sourceEl.appendChild(sourceCode)

      let latestSvgHtml = ''
      let latestError: string | null = null

      const setChatButtonLabel = (hasError: boolean) => {
        const label = hasError ? 'Fix in chat' : 'Add to chat'
        chatBtn.title = label
        chatBtn.setAttribute('aria-label', label)
        chatLabel.textContent = label
      }

      const chatBtn = document.createElement('button')
      chatBtn.type = 'button'
      chatBtn.className = 'mermaid-block-btn mermaid-chat-btn'
      chatBtn.innerHTML = `${MERMAID_CHAT_ICON_SVG}<span class="mermaid-chat-btn-label"></span>`
      const chatLabel = chatBtn.querySelector('.mermaid-chat-btn-label') as HTMLSpanElement
      setChatButtonLabel(false)
      chatBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        openMermaidInChat({
          source: sourceCode.textContent ?? '',
          error: latestError,
        })
      })

      const toggleBtn = document.createElement('button')
      toggleBtn.type = 'button'
      toggleBtn.className = 'mermaid-block-btn mermaid-toggle-btn'
      toggleBtn.title = 'Toggle source'
      toggleBtn.textContent = '{ }'
      let showingSource = false
      toggleBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        showingSource = !showingSource
        diagramEl.style.display = showingSource ? 'none' : latestError ? 'none' : 'block'
        errorEl.style.display = showingSource || !latestError ? 'none' : 'block'
        sourceEl.style.display = showingSource ? 'block' : 'none'
        toggleBtn.classList.toggle('active', showingSource)
      })

      const expandBtn = document.createElement('button')
      expandBtn.type = 'button'
      expandBtn.className = 'mermaid-block-btn mermaid-expand-btn'
      expandBtn.title = 'Expand diagram'
      expandBtn.setAttribute('aria-label', 'Expand diagram to fullscreen')
      expandBtn.innerHTML = MERMAID_EXPAND_ICON_SVG
      expandBtn.style.display = 'none'
      expandBtn.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (latestSvgHtml) {
          openMermaidLightbox({ svgHtml: latestSvgHtml, source: sourceCode.textContent ?? undefined })
        }
      })

      actionsEl.appendChild(chatBtn)
      actionsEl.appendChild(toggleBtn)
      actionsEl.appendChild(expandBtn)

      const renderDiagram = async (text: string) => {
        sourceCode.textContent = text
        errorEl.style.display = 'none'
        diagramEl.innerHTML = ''
        latestSvgHtml = ''
        latestError = null
        expandBtn.style.display = 'none'
        wrapper.classList.remove('mermaid-block--has-error')
        setChatButtonLabel(false)
        showingSource = false
        sourceEl.style.display = 'none'
        toggleBtn.classList.remove('active')

        const result = await renderMermaidDiagram(text)
        if (result.ok) {
          diagramEl.style.display = 'block'
          diagramEl.innerHTML = result.svg
          latestSvgHtml = result.svg
          expandBtn.style.display = 'inline-flex'
        } else {
          latestError = result.error
          wrapper.classList.add('mermaid-block--has-error')
          setChatButtonLabel(true)
          errorEl.replaceChildren()
          const title = document.createElement('p')
          title.className = 'mermaid-error-title'
          title.textContent = 'Diagram could not be rendered'
          const detail = document.createElement('p')
          detail.className = 'mermaid-error-detail'
          detail.textContent = result.error
          const hint = document.createElement('p')
          hint.className = 'mermaid-error-hint'
          hint.innerHTML = 'Use <strong>Fix in chat</strong> or { } to inspect the source.'
          errorEl.append(title, detail, hint)
          errorEl.style.display = 'block'
          diagramEl.style.display = 'none'
          diagramEl.innerHTML = ''
        }
      }

      void renderDiagram(node.textContent)

      wrapper.appendChild(actionsEl)
      wrapper.appendChild(diagramEl)
      wrapper.appendChild(errorEl)
      wrapper.appendChild(sourceEl)

      return {
        dom: wrapper,
        contentDOM: null,
        update(updatedNode) {
          if (updatedNode.type !== node.type) return false
          if (updatedNode.textContent !== node.textContent) {
            void renderDiagram(updatedNode.textContent)
          }
          return true
        },
      }
    }
  },
})

/**
 * TaskItem that does not auto-wrap `[ ]` / `[x]` inside table cells.
 * GFM pipe tables cannot hold block task lists; wrapping there breaks the
 * row on TipTap -> markdown save (orphan `- [x]` + stray `|`).
 */
const taskItemInputRegex = /^\s*(\[([( |x])?\])\s$/

export const TableSafeTaskItem = TaskItem.extend({
  addInputRules() {
    const rule = wrappingInputRule({
      find: taskItemInputRegex,
      type: this.type,
      getAttributes: (match) => ({
        checked: match[match.length - 1] === 'x',
      }),
    })
    const originalHandler = rule.handler
    rule.handler = (props) => {
      const { state, range } = props
      const $from = state.doc.resolve(range.from)
      for (let depth = $from.depth; depth > 0; depth--) {
        const name = $from.node(depth).type.name
        if (name === 'tableCell' || name === 'tableHeader') {
          return null
        }
      }
      return originalHandler(props)
    }
    return [rule]
  },
})

// Custom keyboard extension for list indentation
// Handles Tab/Shift+Tab and Cmd+[/Cmd+] for indent/outdent
export const CustomKeyboardExtension = Extension.create({
  name: 'customKeyboard',

  addKeyboardShortcuts() {
    return {
      'Mod-f': () => {
        _openFindBar?.()
        return true
      },

      // Tab: Indent list item (sink)
      Tab: () => {
        // Try to indent list item
        if (this.editor.commands.sinkListItem('listItem')) {
          return true
        }
        // If not in a list, try task list
        if (this.editor.commands.sinkListItem('taskItem')) {
          return true
        }
        // IMPORTANT: Even if we can't indent (at max depth or not in list),
        // still return true to prevent Tab from leaving the editor
        return true
      },

      // Shift+Tab: Outdent list item (lift)
      'Shift-Tab': () => {
        // Try to outdent list item
        if (this.editor.commands.liftListItem('listItem')) {
          return true
        }
        // If not in a list, try task list
        if (this.editor.commands.liftListItem('taskItem')) {
          return true
        }
        // Prevent shift-tab from navigating away even if can't outdent
        return true
      },

      // Cmd+[ (Mac) or Ctrl+[ (Windows): Outdent
      'Mod-[': () => {
        // Try to outdent
        this.editor.commands.liftListItem('listItem')
        this.editor.commands.liftListItem('taskItem')
        // Always prevent browser back navigation
        return true
      },

      // Cmd+] (Mac) or Ctrl+] (Windows): Indent
      'Mod-]': () => {
        // Try to indent
        this.editor.commands.sinkListItem('listItem')
        this.editor.commands.sinkListItem('taskItem')
        // Always prevent browser forward navigation
        return true
      },
    }
  },
})

// Heading extension that adds slug-based id attributes so the TOC can
// query heading elements by id and track scroll position.
export const HeadingWithId = Heading.extend({
  renderHTML({ node, HTMLAttributes }) {
    const id = slugifyHeading(node.textContent || '')
    return [`h${node.attrs.level}`, { ...HTMLAttributes, id }, 0]
  },
})

// Extension to preserve data-block-id attributes in TipTap nodes
// This allows us to map TipTap blocks back to source line numbers
export const BlockIdExtension = Extension.create({
  name: 'blockId',

  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'codeBlock', 'blockquote', 'orderedList', 'bulletList'],
        attributes: {
          'data-block-id': {
            default: null,
            parseHTML: (element) => element.getAttribute('data-block-id'),
            renderHTML: (attributes) => {
              if (!attributes['data-block-id']) return {}
              return { 'data-block-id': attributes['data-block-id'] }
            },
          },
        },
      },
    ]
  },
})
